<template>
  <div class="news-page">
    <!-- Header Banner Section -->
    <section class="whaybest news-bg">
      <div class="fadeIn">
        <img class="news-img" src="/static/images/news-min.png" data-src="/static/images/news-min.png" alt="News Background" ref="lazyImg" />
        <div class="faq-line"></div>
        <div class="faq-title" style="color: #FFFFFF;">NEWS</div>
      </div>
    </section>

    <!-- News List Section -->
    <section class="news-list">
      <div class="container">
        <div class="row">
          <div class="col-lg-12">
            <!-- 加载状态 -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <p class="mt-3">Loading data...</p>
            </div>
            
            <!-- 错误状态 -->
            <EmptyState 
              v-else-if="error"
              icon="error"
              title="Data Loading Failed"
              :description="error"
              :show-action="true"
              action-text="Reload"
              @action="refreshNews"
            />
            
            <!-- 新闻列表 -->
            <div v-else-if="articles && articles.length > 0" class="news-items">
              <article v-for="item in articles" :key="item.id" class="news-row">
                <div class="news-thumb" @click="viewDetail(item)">
                  <img :src="getImagePath(item.coverImage)" 
                       :data-src="getImagePath(item.coverImage)" 
                       :alt="item.title"
                       @error="handleImageError">
                </div>
                <div class="news-text">
                  <h2 class="news-title" @click.prevent="viewDetail(item)">{{ item.title }}</h2>
                  <div class="news-author-date">
                    {{ item.author }}, {{ formatDate(item.publishTime) }}
                    <span v-if="item.viewCount > 0" class="ml-3">
                      <i class="fa fa-eye"></i> {{ item.viewCount }}
                    </span>
                  </div>
                  <div class="news-excerpt ql-editor" v-html="item.summary"></div>
                  <a v-if="item.content" href="#" @click.prevent="viewDetail(item)" class="news-readmore">Continue Reading</a>
                </div>
              </article>
            </div>
            
            <!-- 空状态 -->
            <EmptyState 
              v-else
              icon="news"
              title="No Content"
              description="No news articles have been published yet"
              :show-action="true"
              action-text="Refresh Page"
              @action="refreshNews"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- 分页组件 -->
    <Pagination 
      v-if="totalCount > pageSize"
      :current-page="currentPage"
      :total-pages="totalPages"
      @page-change="handlePageChange"
    />
  </div>
</template>

<script>
import { lazyLoadingMixin } from '@/mixins/lazyLoading'
import { newsAPI } from '@/utils/api'
import { imgPath } from '@/utils/main'
import EmptyState from '@/components/EmptyState.vue'
import Pagination from '@/components/Pagination.vue'

export default {
  name: 'News',
  mixins: [lazyLoadingMixin],
  components: {
    EmptyState,
    Pagination
  },
  data() {
    return {
      articles: [],
      loading: false,
      error: null,
      fallbackArticles: [],
      // 分页相关状态
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,
      totalPages: 0
    }
  },
  async mounted() {
    document.title = 'News - ANOVATOR'
    await this.fetchNews()
  },
  methods: {
    // 从后端获取新闻数据
    async fetchNews(page = 1) {
      this.loading = true
      this.error = null
      this.currentPage = page
      
      try {
        // 调用API获取新闻列表
        const response = await newsAPI.getNewsList({
          pageIndex: page,
          pageSize: this.pageSize
        })
        // 处理后端返回的数据
        if (response && response.DATA) {
          // 如果返回的是数组格式（旧格式）
          if (Array.isArray(response.DATA)) {
            this.articles = response.DATA.map(item => ({
              ...item,
              // 确保日期字段是Date对象
              publishTime: item.publishTime ? new Date(item.publishTime) : new Date(),
              gmtCreate: item.gmtCreate ? new Date(item.gmtCreate) : new Date(),
              gmtModify: item.gmtModify ? new Date(item.gmtModify) : new Date()
            }))
            this.totalCount = response.total || 0
            this.totalPages = Math.ceil(this.totalCount / this.pageSize)
          } 
        } else {
          // 如果返回数据格式不正确，使用fallback数据
          console.warn('API返回数据格式不正确，使用默认数据')
          this.articles = this.fallbackArticles
          this.totalCount = this.fallbackArticles.length
          this.totalPages = Math.ceil(this.totalCount / this.pageSize)
        }
      } catch (error) {
        console.error('获取新闻数据失败:', error)
        this.error = 'Network connection failed. Unable to retrieve the latest news. Please check your network connection and try again.'
        this.articles = this.fallbackArticles
        this.totalCount = this.fallbackArticles.length
        this.totalPages = Math.ceil(this.totalCount / this.pageSize)
      } finally {
        this.loading = false
      }
    },
    
    // 处理分页变化
    async handlePageChange(page) {
      if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
        await this.fetchNews(page)
        // 滚动到页面顶部
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    
    formatDate(dateObj) {
      try {
        let d;
        if (dateObj instanceof Date) {
          d = dateObj;
        } else {
          d = new Date(dateObj);
        }
        const options = { year: 'numeric', month: 'long', day: 'numeric' }
        return d.toLocaleDateString('en-US', options)
      } catch (e) {
        return dateObj
      }
    },
    
    viewDetail(item) {
      // 异步增加浏览量，不阻塞跳转
      newsAPI.increaseViewCount(item.id).catch(error => {
        console.error('增加浏览量失败:', error)
      })
      
      // 在新窗口打开新闻详情页面
      const url = this.$router.resolve(`/news/${item.id}`).href
      window.open(url, '_blank')
    },
    
    // 刷新新闻数据
    async refreshNews() {
      await this.fetchNews()
    },
    
    // 获取图片路径
    getImagePath(img) {
      return imgPath(img)
    },
    
    // 处理图片加载错误
    handleImageError(event) {
      event.target.src = '/static/images/null-img.png'
      event.target.onerror = () => {
        event.target.src = '/static/images/null-img.png'
      }
    }
  }
}
</script>

<style scoped>
/* 引入Quill编辑器样式 */
@import url('https://cdn.quilljs.com/1.3.6/quill.snow.css');

.news-page {
  min-height: 100vh;
}

.news-list {
  padding: 40px 0;
}

.news-items {
  display: block;
}

.news-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding: 24px 0;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.news-row:last-child {
  border-bottom: none;
  margin-bottom: 40px;
}

.news-thumb {
  flex: 0 0 420px;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.news-thumb:hover {
  opacity: 0.8;
}

.news-thumb img {
  width: 100%;
  height: 240px;
  object-fit: cover;
  background: #1a1919;
}

.image-placeholder {
  width: 100%;
  height: 240px;
  background: #1a1919;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
}

.image-placeholder i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.image-placeholder span {
  opacity: 0.7;
}

.news-text {
  flex: 1;
}

.news-title {
  color: #FFFFFF;
  margin: 0 0 6px;
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
  transition: color 0.3s ease;
}
.news-title:hover {
  color: #EE0B6A;
}

.news-author-date {
  color: rgba(255,255,255,0.6);
  margin-bottom: 12px;
}

.news-excerpt {
  color: rgba(255,255,255,0.85);
  height: 140px;
}

/* 覆盖Quill编辑器样式以适应深色主题 */
.news-excerpt.ql-editor {
  padding: 0;
  color: rgba(255,255,255,0.85);
  background: transparent;
  border: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

.news-excerpt.ql-editor p {
  color: rgba(255,255,255,0.85);
  margin: 0 0 12px 0;
}

.news-excerpt.ql-editor h1,
.news-excerpt.ql-editor h2,
.news-excerpt.ql-editor h3,
.news-excerpt.ql-editor h4,
.news-excerpt.ql-editor h5,
.news-excerpt.ql-editor h6 {
  color: #FFFFFF;
}

.news-excerpt.ql-editor strong {
  color: #FFFFFF;
}

.news-excerpt.ql-editor em {
  color: rgba(255,255,255,0.9);
}

.news-excerpt.ql-editor ul,
.news-excerpt.ql-editor ol {
  color: rgba(255,255,255,0.85);
}

.news-excerpt.ql-editor blockquote {
  border-left: 4px solid #EE0B6A;
  color: rgba(255,255,255,0.9);
  background: rgba(238, 11, 106, 0.1);
}

.news-readmore {
  color: #EE0B6A;
  text-decoration: none;
  font-size: 14px;
}

.news-readmore:hover {
  text-decoration: underline;
}
.ql-editor .ql-align-center {
  padding-bottom: 10px;
}
.ql-editor .ql-align-justify {
  padding-bottom: 10px;
}


/* Responsive */
@media (max-width: 992px) {
  .news-thumb { flex-basis: 360px; }
  .news-thumb img { height: 220px; }
}

@media (max-width: 768px) {
  .news-row {
    flex-direction: column;
  }
  .news-thumb { flex-basis: auto; }
  .news-thumb img { height: auto; }
}

/* Banner image height override */
.faq-img {
  height: 380px;
  width: 100%;
  object-fit: cover;
}
</style>